import {
	type ColumnFilter,
	type ColumnPinningState,
	type ColumnSort,
	type GroupingState,
	type OnChangeFn,
	type RowSelectionState,
	type TableOptions,
	type TableState as TanStackTableState,
	type VisibilityState,
} from "@tanstack/react-table";
import { type Objectish } from "immer";
import { type Dispatch, type SetStateAction, useCallback, useMemo, useRef, useState } from "react";
import { z } from "zod/v4";

import { type DoTransactionFn, useCellServices, useCellState } from "@moment/ui";

import { type DataGridProps } from "./Props";
import { builtinAggregationFns } from "./grouping-aggregations";
import pick from "./pick";
import { getTableMeta } from "./tableModels";
import { type DataGridColumnDef } from "./useColumnState";

const columnsHiddenValueToInternalState = (
	columnsHiddenValue: string[]
): Record<string, boolean> => {
	if (!Array.isArray(columnsHiddenValue)) {
		return {};
	}

	return (columnsHiddenValue || []).reduce((acc, columnId) => {
		acc[columnId] = false;
		return acc;
	}, {});
};

const internalStateToColumnsHiddenValue = (columnVisibility: Record<string, boolean>): string[] => {
	return Object.entries(columnVisibility)
		.filter(([, visible]) => !visible)
		.map(([columnId]) => columnId);
};

/**
 * The table state *NOT* managed by TanStack `useTable`. Title, column definitions, and so on.
 */
export interface TableCustomState {
	title?: string;
	columns?: DataGridColumnDef<unknown, any>[];
	data?: unknown[];
}

export type TableCustomStateUpdater = Partial<{
	[Key in keyof TableCustomState]: SetStateAction<TableCustomState[Key]>;
}>;

/**
 * Passed as the `state` field in the argument to `useTable`. Specifies all the state TanStack table
 * manages, including active filters, grouping columns, hidden columns, selected rows, and the like.
 */
export interface TableHookState extends Partial<TanStackTableState> {
	columnPinning: ColumnPinningState;
	columnVisibility: VisibilityState;
	grouping: GroupingState;
	sorting: ColumnSort[];
	rowSelection: RowSelectionState;
	columnFilters: ColumnFilter[];
	globalFilter: string | undefined;
}

export type TableHookStateUpdater = Partial<{
	[Key in keyof TableHookState]: SetStateAction<TableHookState[Key]>;
}>;

/**
 * All the state needed to render the table, including both the `hookState` (passed to `useTable`)
 * and the `customState` (managed by us, stuff like title, column definitions, etc.)
 */
export interface MaterializedTableState {
	customState: TableCustomState;
	hookState: TableHookState;
}

export type MaterializedTableStateUpdater = Partial<{
	customState: TableCustomStateUpdater;
	hookState: TableHookStateUpdater;
}>;

const persistableColumnDefKeys = new Set([
	"id",
	"accessorKey",
	"header",
	"maxSize",
	"size",
	"minSize",
	"readonly",
	"fieldType",
	"fieldTypeInfo",
	"aggregationFnId",
]);

const columnsSchema = z.array(
	z
		.object({
			id: z.string().optional(),
			accessorKey: z.string().optional(),
			header: z.string().optional(),
			maxSize: z.number().optional(),
			size: z.number().optional(),
			minSize: z.number().optional(),
			readonly: z.boolean().optional(),
			aggregationFnId: z.string().optional(),
		})
		.and(
			z.discriminatedUnion("fieldType", [
				z.object({
					fieldType: z.literal("Auto"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Boolean"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Currency"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Date time"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Date"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Email"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Link to record"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Multiline text"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Number"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Percent"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Person"),
					fieldTypeInfo: z
						.object({
							defaultValue: z
								.union([z.literal("Empty"), z.literal("Current user")])
								.optional(),
						})
						.optional(),
				}),
				z.object({
					fieldType: z.literal("Progress bar"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Reaction"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Emoji reactions"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Table"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Tag"),
					fieldTypeInfo: z
						.object({
							tags: z.array(z.object({ label: z.string(), color: z.string() })),
							defaultValue: z.array(z.string()).optional(),
						})
						.optional(),
				}),
				z.object({
					fieldType: z.literal("Text"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Time distance to now"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("Time"),
					fieldTypeInfo: z.undefined().optional(),
				}),
				z.object({
					fieldType: z.literal("URL"),
					fieldTypeInfo: z
						.object({
							textAccessorKey: z.string().optional(),
						})
						.optional(),
				}),
				z.object({
					fieldType: z.undefined(),
					fieldTypeInfo: z.undefined().optional(),
				}),
			])
		)
);

export const persistableTableStateSchema = z.object({
	schemaVersion: z.literal("v1alpha1"),
	customState: z.object({
		title: z.string().optional(),
		columns: columnsSchema.optional(),
		data: z.array(z.unknown()).optional(),
	}),
	hookState: z.object({
		columnPinning: z
			.object({
				left: z.array(z.string()).optional(),
				right: z.array(z.string()).optional(),
			})
			.optional(),
		columnVisibility: z.record(z.string(), z.boolean()).optional(),
		grouping: z.array(z.string()).optional(),
		sorting: z.array(z.object({ desc: z.boolean(), id: z.string() })).optional(),
		rowSelection: z.record(z.string(), z.boolean()).optional(),
		columnFilters: z.array(z.object({ id: z.string(), value: z.unknown() })).optional(),
		globalFilter: z.string().optional(),
	}),
});
export type PersistableTableState = z.infer<typeof persistableTableStateSchema>;

const makePersistTableStateTransaction = (
	newCustomState: TableCustomState,
	newHookState: TableHookState
): DoTransactionFn<Objectish> => {
	return (prevState: Objectish) => {
		const collaborativeState: PersistableTableState = {
			schemaVersion: "v1alpha1",
			customState: {
				data: newCustomState?.data,
				title: newCustomState?.title,
				columns: newCustomState?.columns?.map((column) => {
					const newColumn = Object.assign({}, column);
					for (const key of Object.keys(newColumn)) {
						if (!persistableColumnDefKeys.has(key)) {
							delete newColumn[key];
						}

						// Don't persist the default min/max size, it confuses users and is
						// redundant.
						if (newColumn.minSize === 20) {
							delete newColumn["minSize"];
						}
						if (newColumn.maxSize === Number.MAX_SAFE_INTEGER) {
							delete newColumn["maxSize"];
						}
					}
					return newColumn;
				}),
			},
			hookState: {
				...pick(
					newHookState,
					"columnPinning",
					"columnVisibility",
					"grouping",
					"sorting",
					"columnFilters",
					"globalFilter"
				),
			},
		};
		Object.assign(prevState, collaborativeState);
		return prevState;
	};
};

const DEFAULT_TABLE_STATE: TableHookState = {
	columnPinning: {},
	columnVisibility: {},
	grouping: [],
	sorting: [],
	rowSelection: {},
	columnFilters: [],
	globalFilter: undefined,
};

const propsToMergeablePartialTableState = (
	columns: PersistableTableState["customState"]["columns"] | undefined,
	columnVisibility: string[] | undefined,
	grouping: string | undefined,
	sorting: ColumnSort[] | undefined,
	globalFilter: string | undefined
) =>
	Object.assign(
		{},
		columns !== undefined && { columns },
		columnVisibility !== undefined && {
			columnVisibility: columnsHiddenValueToInternalState(columnVisibility),
		},
		grouping !== undefined && { grouping: [grouping] },
		sorting !== undefined && { sorting },
		// Explicit `undefined` check here is important to ensure global filter is unset if the
		// string is "".
		globalFilter !== undefined && { globalFilter }
	);

const normalizeColumns = (columns: DataGridColumnDef<unknown, any>[]) => {
	return columns.map<DataGridColumnDef<unknown, any>>((column) => {
		let aggregationFn = column.aggregationFn;
		if (!aggregationFn && column.aggregationFnId) {
			aggregationFn = builtinAggregationFns.find(
				(fun) => fun.label === column.aggregationFnId
			)?.value;
		}
		return { ...column, aggregationFn };
	});
};

const useMaterializedTableState = (
	activeTableProps: DataGridProps<unknown, unknown> | null
): {
	materializedTableState: MaterializedTableState;
	updateMaterializedTableState: (
		stateUpdates: MaterializedTableStateUpdater | undefined
	) => Promise<MaterializedTableState>;
	persistedStateHydrationError: z.ZodError | undefined;
} => {
	const [_persistedState, doPersistState] = useCellState();
	const [persistedState, persistedStateHydrationError] = useMemo(() => {
		const cellState = persistableTableStateSchema.safeParse(_persistedState);
		return [cellState.data, cellState.error];
	}, [_persistedState]);

	const [materializedTableCustomState, _setMaterializedTableCustomState] =
		useState<TableCustomState>(() =>
			Object.assign({
				data:
					activeTableProps?.dataValue ??
					persistedState?.customState?.data ??
					activeTableProps?.defaultDataValue,
				title:
					activeTableProps?.titleValue ??
					persistedState?.customState?.title ??
					activeTableProps?.defaultTitleValue,
				columns: normalizeColumns(
					// @ts-ignore - TODO: fix this
					(activeTableProps?.columnsValue as DataGridColumnDef<unknown, any>[]) ??
						persistedState?.customState?.columns ??
						activeTableProps?.defaultColumnsValue
				),
			})
		);
	const setMaterializedTableCustomState: Dispatch<SetStateAction<TableCustomState>> = useCallback(
		(update) =>
			_setMaterializedTableCustomState((prev) => {
				const result = update instanceof Function ? update(prev) : update;
				if (result.columns) {
					result.columns = normalizeColumns(result.columns);
				}
				return result;
			}),
		[]
	);
	const materializedTableCustomStateRef = useRef(materializedTableCustomState);

	const [materializedTableHookState, _setMaterializedTableHookState] = useState<TableHookState>(
		() =>
			Object.assign(
				DEFAULT_TABLE_STATE,
				propsToMergeablePartialTableState(
					activeTableProps?.columnsValue,
					activeTableProps?.columnsHiddenValue,
					activeTableProps?.groupingValue,
					activeTableProps?.sortingValue,
					activeTableProps?.searchQueryValue
				),
				persistedState?.hookState || {},
				propsToMergeablePartialTableState(
					activeTableProps?.defaultColumnsValue,
					activeTableProps?.defaultColumnsHiddenValue,
					activeTableProps?.defaultGroupingValue,
					activeTableProps?.defaultSortingValue,
					activeTableProps?.defaultSearchQueryValue
				)
			)
	);
	const materializedTableHookStateRef = useRef(materializedTableHookState);

	const updateMaterializedTableState = useCallback(
		async (stateUpdates: MaterializedTableStateUpdater | undefined) => {
			function update<T>(updater: SetStateAction<T>, currentState: T) {
				const ret = updater instanceof Function ? updater(currentState) : updater;
				return ret;
			}

			const newCustomState: TableCustomState = Object.assign(
				{}, // New object reference to trigger a re-render.
				materializedTableCustomStateRef.current,
				stateUpdates?.customState?.data !== undefined &&
					!activeTableProps?.dataValue && {
						data: update(
							stateUpdates.customState.data,
							materializedTableCustomStateRef.current.data
						),
					},
				stateUpdates?.customState?.title !== undefined &&
					!activeTableProps?.titleValue && {
						title: update(
							stateUpdates.customState.title,
							materializedTableCustomStateRef.current.title
						),
					},
				stateUpdates?.customState?.columns !== undefined &&
					!activeTableProps?.columnsValue && {
						columns: update(
							stateUpdates.customState.columns,
							materializedTableCustomStateRef.current.columns
						),
					}
			);
			materializedTableCustomStateRef.current = newCustomState;
			setMaterializedTableCustomState(newCustomState);

			const newHookState: TableHookState = Object.assign(
				{}, // New object reference to trigger a re-render.
				materializedTableHookStateRef.current,
				stateUpdates?.hookState?.columnVisibility !== undefined &&
					!activeTableProps?.columnsHiddenValue && {
						columnVisibility: update(
							stateUpdates.hookState.columnVisibility,
							materializedTableHookStateRef.current.columnVisibility
						),
					},
				stateUpdates?.hookState?.grouping !== undefined &&
					!activeTableProps?.groupingValue && {
						grouping: update(
							stateUpdates.hookState.grouping,
							materializedTableHookStateRef.current.grouping
						),
					},
				stateUpdates?.hookState?.sorting !== undefined && {
					sorting: update(
						stateUpdates.hookState.sorting,
						materializedTableHookStateRef.current.sorting
					),
				},
				// Explicit `undefined` check here is important to ensure global filter is unset if
				// the string is "".
				stateUpdates?.hookState?.globalFilter !== undefined && {
					globalFilter: update(
						stateUpdates.hookState.globalFilter,
						materializedTableHookStateRef.current.globalFilter
					),
				}
			);

			//
			// IMPORTANT: Trick the table into re-computing aggregations!
			// Aggregations are memoized on the grouping state. cf.,
			// https://github.com/TanStack/table/blob/d7f99a1ad133881cca4d16cb6d84f7676f0e7369/packages/table-core/src/utils/getGroupedRowModel.ts#L10
			//
			if (stateUpdates?.customState?.columns && !stateUpdates.hookState?.grouping) {
				newHookState.grouping = [...newHookState.grouping];
			}

			if (newHookState.grouping.length > 1) {
				// We only allow one grouping at a time, so always remove other groupings and choose
				// the last one appended.
				newHookState.grouping = [newHookState.grouping[newHookState.grouping.length - 1]!];
			}

			materializedTableHookStateRef.current = newHookState;
			_setMaterializedTableHookState(newHookState);

			await doPersistState(makePersistTableStateTransaction(newCustomState, newHookState));

			//
			// Custom state.
			//

			// FIXME: eventually we should support this.
			//
			// stateUpdates?.customState?.data !== undefined &&
			// 	activeTableProps?.onDataValueChange?.(
			// 		stateUpdates.customState.data instanceof Function
			// 			? stateUpdates.customState.data
			// 			: (data) => {
			// 					Object.keys(data).forEach((key) => delete data[key]);
			// 					Object.assign(data, stateUpdates.customState?.data ?? []);
			// 			  }
			// 	);
			stateUpdates?.customState?.title !== undefined &&
				activeTableProps?.onTitleValueChange?.(newCustomState.title ?? "");
			stateUpdates?.customState?.columns !== undefined &&
				activeTableProps?.onColumnsValueChange?.(newCustomState.columns ?? []);

			//
			// Hook state.
			//

			stateUpdates?.hookState?.columnVisibility !== undefined &&
				activeTableProps?.onColumnsHiddenValueChange?.(
					internalStateToColumnsHiddenValue(newHookState.columnVisibility)
				);
			stateUpdates?.hookState?.grouping?.[0] !== undefined &&
				activeTableProps?.onGroupingValueChange?.(newHookState.grouping[0] ?? "");
			stateUpdates?.hookState?.sorting !== undefined &&
				activeTableProps?.onSortingValueChange?.(newHookState.sorting);

			// We still want to unset the filter if the string is "".
			stateUpdates?.hookState?.globalFilter !== undefined &&
				activeTableProps?.onSearchQueryValueChange?.(newHookState.globalFilter);

			return { customState: newCustomState, hookState: newHookState };
		},
		[
			activeTableProps,
			doPersistState,
			materializedTableCustomStateRef,
			setMaterializedTableCustomState,
		]
	);

	return useMemo(() => {
		return {
			materializedTableState: {
				customState: materializedTableCustomState,
				hookState: materializedTableHookState,
			},
			updateMaterializedTableState,
			persistedStateHydrationError,
		};
	}, [
		materializedTableCustomState,
		materializedTableHookState,
		persistedStateHydrationError,
		updateMaterializedTableState,
	]);
};

/**
 * Hook to manage the source-of-truth state of a single TanStack Table. This includes selected rows,
 * grouping columns, hidden columns, global filters, column filters, and all such other visible and
 * interactive state of the table.
 *
 * This state can be used both as the source of truth for controlling state of the active DataGrid
 * tab, as well as keeping a history of tab states for easy navigation back and forth between views.
 */
export const useTableState = (
	activeTableProps: DataGridProps<unknown, unknown> | null
): {
	materializedTableState: MaterializedTableState;
	cellStateError: z.ZodError | undefined;
	updateMaterializedTableState: (state?: MaterializedTableStateUpdater) => void;
	reactTableOpts: Pick<
		TableOptions<unknown>,
		| "data"
		| "state"
		| "columns"
		| "meta"
		| "onColumnFiltersChange"
		| "onGlobalFilterChange"
		| "onColumnVisibilityChange"
		| "onGroupingChange"
		| "onSortingChange"
		| "onColumnPinningChange"
		| "onRowSelectionChange"
	>;
} => {
	const cellServices = useCellServices();

	const { materializedTableState, updateMaterializedTableState, persistedStateHydrationError } =
		useMaterializedTableState(activeTableProps);

	const setColumns: Dispatch<SetStateAction<DataGridColumnDef<unknown, unknown>[] | undefined>> =
		useCallback(
			(columnsUpdater) =>
				updateMaterializedTableState({ customState: { columns: columnsUpdater } }),
			[updateMaterializedTableState]
		);

	const onDataValueChange = useCallback(
		(mutate: (data: unknown[]) => void) =>
			updateMaterializedTableState({
				customState: {
					data: (data) => {
						// IMPORTANT: when this is hydrated from disk the object is often readonly.
						// Clone it to avoid throwing writes away.
						const newData = structuredClone(data ?? DEFAULT_DATA_VALUE);
						mutate(newData);
						return Object.assign([], newData ?? DEFAULT_DATA_VALUE);
					},
				},
			}),
		[updateMaterializedTableState]
	);

	const onColumnFiltersChange: OnChangeFn<ColumnFilter[]> = useCallback(
		(columnFilters) => updateMaterializedTableState({ hookState: { columnFilters } }),
		[updateMaterializedTableState]
	);
	const onGlobalFilterChange: OnChangeFn<string | undefined> = useCallback(
		(globalFilter) => updateMaterializedTableState({ hookState: { globalFilter } }),
		[updateMaterializedTableState]
	);
	const onColumnVisibilityChange: OnChangeFn<Record<string, boolean>> = useCallback(
		(columnVisibility) => updateMaterializedTableState({ hookState: { columnVisibility } }),
		[updateMaterializedTableState]
	);
	const onGroupingChange: OnChangeFn<GroupingState> = useCallback(
		(grouping) => updateMaterializedTableState({ hookState: { grouping } }),
		[updateMaterializedTableState]
	);
	const onSortingChange: OnChangeFn<ColumnSort[]> = useCallback(
		(sorting) => updateMaterializedTableState({ hookState: { sorting } }),
		[updateMaterializedTableState]
	);
	const onColumnPinningChange = useCallback(
		(columnPinning: object) => updateMaterializedTableState({ hookState: { columnPinning } }),
		[updateMaterializedTableState]
	);
	const onRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
		(rowSelection) => updateMaterializedTableState({ hookState: { rowSelection } }),
		[updateMaterializedTableState]
	);

	return useMemo(
		() => ({
			materializedTableState,
			cellStateError: persistedStateHydrationError,
			updateMaterializedTableState,
			reactTableOpts: {
				// CRITICALLY IMPORTANT: Prevent infinite re-renders by ensuring the `data` prop is
				// *ALWAYS* `DEFAULT_DATA_VALUE`.
				data: materializedTableState.customState.data ?? DEFAULT_DATA_VALUE,
				// data: activeTableProps?.dataValue ?? activeTableProps?.defaultDataValue ?? [],
				state: materializedTableState.hookState,
				columns: materializedTableState.customState.columns ?? [],
				meta: getTableMeta(
					setColumns,
					onDataValueChange,
					activeTableProps?.defaultDataValue,
					cellServices.users,
					cellServices.currentUser ?? undefined,
					cellServices.documentInteractionMode
				),
				onColumnFiltersChange,
				onGlobalFilterChange,
				onColumnVisibilityChange,
				onGroupingChange,
				onSortingChange,
				onColumnPinningChange,
				onRowSelectionChange,
			},
		}),
		[
			activeTableProps?.defaultDataValue,
			cellServices.currentUser,
			cellServices.documentInteractionMode,
			cellServices.users,
			materializedTableState,
			onColumnFiltersChange,
			onColumnPinningChange,
			onColumnVisibilityChange,
			onDataValueChange,
			onGlobalFilterChange,
			onGroupingChange,
			onRowSelectionChange,
			onSortingChange,
			persistedStateHydrationError,
			setColumns,
			updateMaterializedTableState,
		]
	);
};

const DEFAULT_DATA_VALUE = [];
